import React, { PropsWithChildren, useEffect } from 'react';
import styled from 'styled-components/macro';
import { defaultTransition, colorValue } from '../../../../lib/theme';
import { InternalItem, StyledIndicator, StyledSidebarLink } from './SidebarLinkItem';
import { useRouteMatch } from 'react-router-dom';
import { useSidebarContext } from '../Sidebar/SidebarContext';

import { base } from '@sdxcorp/sdx-dds-gui-lib';

const {
  atoms: {
    Icons: { ChevronDown, ChevronUp },
  },
} = base;

interface Props {
  id: string;
  label: string;
  icon?: JSX.Element;
  to?: string;
  match: string;
  matchExact?: boolean;
}

const StyledSidebarLinkWrapper = styled.div<{ matches: boolean; children?: React.ReactNode; id?: string }>`
  transition: ${defaultTransition('background-color')};
  display: flex;
  justify-content: flex-start;
  background-color: ${({ matches, theme }) => (matches ? colorValue(theme, c => c['grey-90']) : 'colorValue')};

  :hover {
    ${StyledIndicator} {
      background-color: ${({ matches, theme }) => (!matches ? colorValue(theme, c => c['grey-85']) : 'colorValue')};
    }
    ${StyledSidebarLink} {
      background-color: ${({ matches }) => matches && 'transparent'};
    }
  }
`;

const StyledLinkContainer = styled.div<{ matches: boolean; collapsed: boolean; children?: React.ReactNode }>`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  padding-bottom: ${({ matches, collapsed }) => (matches && !collapsed ? '16px' : '0px')};
`;

const CategoryItem: React.FC<PropsWithChildren<Props>> = ({ id, label, icon, to, match, matchExact = false, children }) => {
  const matchResult = useRouteMatch(match);
  const matches = !!matchResult && (matchResult.isExact || matchExact === false);
  const { sidebarExpanded, expandedItems, setItemExpanded, toggleItemExpanded } = useSidebarContext();
  const isExpandable = !!children;
  const itemExpanded = isExpandable && expandedItems.includes(id);
  const Indicator = itemExpanded ? ChevronUp : ChevronDown;
  const handleClick = (e: React.MouseEvent) => {
    if (isExpandable) {
      e.preventDefault();
      toggleItemExpanded(id);
    }
  };
  useEffect(() => {
    if (matches) {
      setItemExpanded(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);
  return (
    <StyledSidebarLinkWrapper id={`sb-item-${id}`} matches={matches}>
      <StyledIndicator matches={matches} />
      <StyledLinkContainer collapsed={!sidebarExpanded} matches={matches && itemExpanded && !!children}>
        <InternalItem
          testId={id}
          depth={0}
          label={label}
          icon={icon}
          indicator={(!!children && <Indicator id={`sb-item-${id}-indicator`} />) || undefined}
          to={to}
          match={match}
          matchExact={matchExact}
          onClick={handleClick}
          isLink={!isExpandable}
        />
        {itemExpanded && sidebarExpanded && children}
      </StyledLinkContainer>
    </StyledSidebarLinkWrapper>
  );
};

export default CategoryItem;
